import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { FloatingMenuOption } from 'src/app/common/components/master-floating-menu/model/master-floating-model';
import { VoucherModel } from './models/voucher.model';
import { VoucherService } from './voucher.service';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { ColumnFilter, RequestData, SearchSort } from 'src/app/common/models/request-data.model';
import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { SelectionModel } from '@angular/cdk/collections';
import { FilterListItemModel, SearchFilterComponent, SearchParam } from 'src/app/common/components/search-filter/search-filter.component';
import { Router } from '@angular/router';
import { StateManagerService } from 'src/app/common/services/state.manager.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, SortDirection } from '@angular/material/sort';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-voucher',
  standalone: false,
  templateUrl: './voucher.component.html',
  styleUrl: './voucher.component.scss'
})
export class VoucherComponent {

  voucherList: VoucherModel[] = [];
  showSearch: boolean = false;
  isLoading = true;
  filter: FilterListItemModel[] = [
    {
      title: "Code",
      column: "code",
    },
    {
      title: "Name",
      column: "name",
    },
    {
      title: "Voucher Type",
      column: "voucher_type",
      type: 'list',
      operators: [
        { title: "in", operator: "=" },
        { title: "not in", operator: "!=" }
      ],
      values: [] // Populated dynamically in loadData()
    },
    {
      title: "Value",
      column: "value",
    },

  ];
  displayedColumns = ['code', 'name', 'description', 'voucher_type', 'value', 'action'];
  pagingData = { length: 0, pageSize: 50, pageIndex: 0 };
  menuOption: FloatingMenuOption = {
    add: true,
    search: false,
    export: true,
    more: true
  }

  isHandset: boolean = false;
  searchColumFilter: SearchParam = {};
  columnSortData: SearchSort[] = [];
  selectedItem?: VoucherModel;
  showSummaryPanel: boolean = false;
  selection = new SelectionModel<VoucherModel>(true, []);
  dataSource = new MatTableDataSource(this.voucherList);
  IsDownloading: boolean = false;
  permission: any;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private voucherService: VoucherService,
    public dialog: MatDialog,
    private snackBarService: SnackBarService,
    private router: Router,
    public permissionService: PermissionService,
    private stateManagerService: StateManagerService,
  ) {
    this.voucherList = [];
    this.permission = permissionService.getPagePermission("voucher");
  }

  /**
   * Initialize the device model
   */
  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe((state: BreakpointState) => {
      if (state.matches) {
        this.isHandset = true;
      } else {
        this.isHandset = false;
      }
    });
  }

  /** 
   * Set the datasource
   */
  setDataSource(voucherList: VoucherModel[]) {
    this.dataSource = new MatTableDataSource(voucherList);
    this.dataSource.sort = this.sort;
  }

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('searchFilter') searchFilter!: SearchFilterComponent;

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.loadPreviousState();
    });
  }

  /**
   * Loads the previous state if exists
   */
  loadPreviousState(): void {
    var previousState = this.stateManagerService.getState(this.constructor.name);

    if (previousState !== undefined && this.searchFilter != undefined) {
      if (previousState.state.search.filters.scope) {
        this.pagingData.pageIndex = previousState.state.search.filters.scope.offset / previousState.state.search.filters.scope.limit;
        this.pagingData.pageSize = previousState.state.search.filters.scope.limit;
      }

      if (previousState.state.search.sort != undefined && previousState.state.search.sort.length > 0) {
        this.setSortColumn(previousState.state.search.sort[0].column, previousState.state.search.sort[0].order);
      }

      this.searchColumFilter = JSON.parse(JSON.stringify(previousState.state.search.filters));
      this.searchFilter.setSearchFilter(this.searchColumFilter, true);
    } else {
      this.setSortColumn('name', 'asc');
      this.loadData();
    }
  }

  /**
   * Sets the sort column
   * @param column 
   * @param direction 
   */
  setSortColumn(column: string, direction: SortDirection): void {
    this.columnSortData.length = 0;
    this.columnSortData.push({ column: column, order: direction });
    this.sort.sort({
      id: column,
      start: direction,
      disableClear: false,
    });
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.voucherList.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.voucherList);
  }

  /**
   * Create sort order
   * @returns sort criteria
   */
  getSort(): SearchSort[] {
    return this.columnSortData;
  }

  /**
   * Get voucher type label for display
   */
  getVoucherTypeLabel(value: number): string {
    const option = [
      { title: "St. Pierre's", value: 1 },
      { title: "Cash Back", value: 2 },
      { title: "Discount", value: 3 },
      { title: "Gift Card", value: 4 }
    ].find(opt => opt.value == value);

    return option ? option.title : value?.toString() || '';
  }

  /**
   * Builds the request data to be send to API
   * @returns Request Data
   */
  buildRequestData(): RequestData {
    let searchParam = this.getSearchParam();
    let requestData: RequestData = {
      search: {
        filters: {
          simpleSearch: searchParam.freeSearch,
          advSearch: [
            ...(searchParam.advSearch || []),
            ...this.getDefaultSearchParams(searchParam.advSearch),
          ],
          scope: {
            limit: this.pagingData.pageSize,
            offset: (this.pagingData.pageIndex * this.pagingData.pageSize),
          }
        },
        sort: this.getSort(),
      }
    };
    return requestData;
  }

  /**
   * Check and apply default filter for is_system = 0.
   **/
  getDefaultSearchParams(searchFilter: any): ColumnFilter[] {
    var defFilter: any = [];
    if (
      searchFilter == undefined ||
      !searchFilter.some((sf: any) => sf.column == "is_system")
    ) {
      defFilter.push({
        column: "is_system",
        operator: "=",
        value: "0",
      });
    }
    return defFilter;
  }

  /**
   * Set the column filter and reload the data
   * @param columFilter 
   */
  onFilterApplied(columFilter: any): void {
    this.searchColumFilter = columFilter;
    this.loadData();
  }

  /**
   * On delete menu item selected
   * Confirm the action and call the api to update
   */
  onDelete(voucherInfo: VoucherModel): void {
    const voucherId = voucherInfo.id;
    const message = `Are you sure, you want to delete ` + (voucherInfo.name + "?");
    const dialogData = new ConfirmDialogModel("Confirm Deletion", message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      // console.log(dialogResult);
      if (dialogResult) {
        this.voucherService.delete(voucherInfo).subscribe({
          next: (response) => {
            if (response.status == 'SUCCESS') {
              let snckBarData: SnackBarData = {
                type: 'success',
                title: 'Saved',
                message: 'The record for ' + voucherInfo.name + " has been deleted successfully."
              }
              this.snackBarService.openSnackBar(snckBarData);
              this.loadData();
            } else {
              let snackBarData: SnackBarData = {
                message: response.message,
                title: 'Failed!!!',
                type: 'error'
              }
              this.snackBarService.openSnackBar(snackBarData);
            }
          },
          error: (error) => {
            let snackBarData: SnackBarData = {
              message: 'Failed to update. Connection to the server failed',
              title: 'Failed!!!',
              type: 'error'
            }
            this.snackBarService.openSnackBar(snackBarData);
          }
        });
      }
    });
  }

  /**
   * Loads the data based on the conditions
   */
  loadData(): void {
    this.isLoading = true;
    let requestData = this.buildRequestData();
    let response = this.voucherService.getVoucherList(requestData).subscribe(response => {
      if (response.status == 'SUCCESS') {
        const vouchers = response.data.vouchers;
        this.setDataSource(vouchers);
        this.pagingData.length = response.data.total_count;

        // Dynamically populate Voucher Type Advanced Search Filter
        this.populateVoucherTypeFilter(vouchers);

        if (this.pagingData.length == 0) {
          let snackBarData: SnackBarData = {
            message: 'No data found. Please remove or change the filter if any.',
            title: 'No Data!!!',
            type: 'warn'
          }
          this.snackBarService.openSnackBar(snackBarData);
          this.selectedItem = undefined;
        } else {
          this.stateManagerService.setState(this.constructor.name, requestData);
          this.onRowSelected(this.voucherList[0])
        }
      } else {
        let snackBarData: SnackBarData = {
          message: response.message,
          title: 'Failed!!!',
          type: 'error'
        }
        this.snackBarService.openSnackBar(snackBarData);
      }
      this.isLoading = false;
    });
  }

  /**
   * Populate voucher type filter values dynamically 
   */
  populateVoucherTypeFilter(vouchers: any[]): void {
    const voucherTypeFilter = this.filter.find(f => f.column === "voucher_type");
    if (voucherTypeFilter) {
      // Find distinct voucher_type values from current voucher list
      const distinctTypes = [...new Set(vouchers.map(v => v.voucher_type))];

      // Map to proper values, excluding null/undefined
      const validTypes = distinctTypes
        .filter(t => t !== null && t !== undefined)
        .sort();

      // Clear existings values
      voucherTypeFilter.values = [];

      // Add actual existing types to filter
      validTypes.forEach(typeValue => {
        voucherTypeFilter.values?.push({
          title: this.getVoucherTypeLabel(typeValue as number),
          value: Number(typeValue)
        });
      });
    }
  }

  /**
   * On voucherInfo selected
   * @param voucherInfo 
   */
  onRowSelected(voucherInfo: VoucherModel): void {
    this.selectedItem = voucherInfo;
    this.showSummaryPanel = true;
  }

  /**
   * Set the sort param and reload the data
   * @param $event sort data
   * {"active": "email","direction": "asc"}
   */
  sortData($event: any): void {
    var sd = this.sort;
    this.columnSortData.length = 0;
    this.columnSortData.push({ column: sd.active, order: sd.direction });
    this.loadData();
  }

  /**
   * Creates the column filter conditions based on search criteria
   * @returns Filter conditions for columns
   */
  getSearchParam(): SearchParam {
    return this.searchColumFilter;
  }

  /**
   * On edit the item
   * Show the edit dialog
   */
  onEdit(voucherInfo: VoucherModel): void {
    this.router.navigate(['promotion/voucher-edit', voucherInfo.id]);
  }

  /**
   * @param event 
   * When page size is changed update paging data
   */
  onePageEvent(event: any): void {
    this.pagingData.pageIndex = event.pageIndex;
    this.pagingData.pageSize = event.pageSize;
    this.loadData();
  }

  /**
   * On Add clicked
   * Show the dialog to enter new voucher
   */
  addClicked(): void {
    this.router.navigate(['promotion/voucher-edit', 0]);
  }

  exportClicked() {
    if (this.pagingData.length === 0) {
      let snackBarData: SnackBarData = {
        message: 'No data found to export. Please remove or change the filter if any.',
        title: 'No Data!!!',
        type: 'warn'
      };
      this.snackBarService.openSnackBar(snackBarData);
      return;
    }
    this.isLoading = true;
    let requestData = this.buildRequestData();
    this.IsDownloading = true;

    this.voucherService['downloadExcel'](requestData).subscribe({
      next: (response: Blob) => {
        const reader = new FileReader();

        reader.onload = () => {
          try {
            const text = reader.result as string;
            const json = JSON.parse(text);

            if (json.status === 'NO_DATA_FOUND') {
              let snackBarData: SnackBarData = {
                message: 'No data found for the selected criteria.',
                title: 'No Data Found!',
                type: 'warn'
              };
              this.IsDownloading = false;
              this.snackBarService.openSnackBar(snackBarData);
              return;
            }

            if (json.status === 'ERROR' || json.status === 'FAILED') {
              let snackBarData: SnackBarData = {
                message: json.message || 'An error occurred while generating the report.',
                title: 'Error!',
                type: 'error'
              };
              this.IsDownloading = false;
              this.snackBarService.openSnackBar(snackBarData);
              return;
            }

          } catch (e) {
            if (response.size === 0 || response.size < 100) {
              let snackBarData: SnackBarData = {
                message: 'No data available for export.',
                title: 'No Data Found!',
                type: 'warn'
              };
              this.IsDownloading = false;
              this.snackBarService.openSnackBar(snackBarData);
              return;
            }

            // Valid file - proceed with download
            const url = window.URL.createObjectURL(response);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'voucher_list.csv';
            link.click();
            window.URL.revokeObjectURL(url);
            this.IsDownloading = false;

            let snackBarData: SnackBarData = {
              message: 'Voucher list downloaded successfully.',
              title: 'Success!',
              type: 'success'
            };
            this.snackBarService.openSnackBar(snackBarData);
          }
          this.isLoading = false;
        };

        reader.readAsText(response);
      },
      error: (error: any) => {
        this.IsDownloading = false;
        let snackBarData: SnackBarData = {
          message: 'Failed to export. Connection to the server failed',
          title: 'Failed!!!',
          type: 'error'
        };
        this.snackBarService.openSnackBar(snackBarData);
        this.isLoading = false;
      }
    });
  }
}
